import type { InputT } from "@patient-watch/types";
import type { OptionRow } from "./resolveDictionary";

/**
 * "Add your own option": a choice a respondent added to a question for themselves ("Other", "Add option").
 * It is private: offered as a choice only to whoever added it, and shown as an answer to anyone who can see
 * the answer. Its id is also its saved value, so a rename shows on every answer that uses it.
 */
export interface OwnOptionT {
    option_id: string;
    input_id: string;
    label: string;
    /** Deleted in this session: hidden, though the page's rows may hold it until they reload. */
    deleted?: boolean;
}

export interface OptionViewer {
    /** The signed-in respondent, or null on an anonymous public link. */
    userId: string | null | undefined;
    /** The entry being filled in. Anonymous respondents' own options are tied to it. */
    entryId: string | null | undefined;
}

/**
 * Whether an own option stored in this browser belongs to this viewer. The store is shared by every account
 * that signs in here, so another account's options must not be offered (or editable) on this one.
 */
export function isViewersStoredOption(
    option: { user_id: string | null; entry_id: string },
    viewer: OptionViewer
): boolean {
    if (viewer.userId) return option.user_id === viewer.userId;
    return option.user_id === null && Boolean(viewer.entryId) && option.entry_id === viewer.entryId;
}

/** Own options sort after the standard choices, in the order they were added. */
const OWN_SORT_START = 1_000_000;

/** An own option as an option row, so it resolves like any other choice. */
export function toOwnOptionRow(option: OwnOptionT, index: number): OptionRow {
    return {
        option_id: option.option_id,
        input_id: option.input_id,
        dictionary_id: null,
        parent_option_id: null,
        value: option.option_id,
        code: null,
        label: option.label,
        description: null,
        icon: null,
        keywords: [],
        group_label: null,
        is_disabled: false,
        sort_order: OWN_SORT_START + index,
        metadata: {},
        translations: {},
        created_at: "",
        is_private: true,
        created_by: null,
        entry_id: null,
        option_oid: null,
        sas_label: null,
    };
}

/** An option row this respondent may pick. Rows of another input were passed through by options_from. */
function isVisibleRow(row: OptionRow, inputId: string, viewer: OptionViewer, picked: ReadonlySet<string>): boolean {
    if (!row.is_private || row.input_id !== inputId) return true;
    if (viewer.userId && row.created_by === viewer.userId) return true;
    if (viewer.entryId && row.entry_id === viewer.entryId) return true;
    return picked.has(String(row.value));
}

/**
 * The input's choices as this respondent sees them: the standard choices, their own options (added by them, or
 * in this entry) and any choice already in `picked`, so a saved answer keeps its text. Other respondents'
 * private options are left out. This is a filter on screen, not RLS: private options stay readable through the
 * API (sponsor decision, 2026-10-21). `known` adds own options not in the rows yet (just added, or on an
 * anonymous link, where they are saved only on submit). Inputs without option rows are returned unchanged.
 */
export function withOwnOptions(
    input: InputT,
    viewer: OptionViewer,
    picked: readonly string[],
    known: readonly OwnOptionT[]
): InputT {
    if (!Array.isArray(input.option_rows)) return input;
    const pickedSet = new Set(picked);
    const deleted = new Set(known.filter((option) => option.deleted).map((option) => option.option_id));
    // A label known in this session (just renamed) is newer than the row loaded with the page.
    const labels = new Map(known.map((option) => [option.option_id, option.label]));
    const rows = input.option_rows
        .filter((row) => !deleted.has(row.option_id) && isVisibleRow(row, input.input_id, viewer, pickedSet))
        .map((row) => {
            const label = row.is_private ? labels.get(row.option_id) : undefined;
            return label !== undefined && label !== row.label ? { ...row, label } : row;
        });
    const present = new Set(rows.map((row) => row.option_id));
    const added = known.filter(
        (option) => option.input_id === input.input_id && !option.deleted && !present.has(option.option_id)
    );
    const unchanged = rows.length === input.option_rows.length && rows.every((row, i) => row === input.option_rows![i]);
    if (added.length === 0 && unchanged) return input;
    const ownCount = rows.filter((row) => row.is_private).length;
    return {
        ...input,
        option_rows: [...rows, ...added.map((option, index) => toOwnOptionRow(option, ownCount + index))],
    };
}

/** The respondent's own options on this input (theirs, or added in this entry), for the add / edit list. */
export function ownOptionsOf(input: InputT, viewer: OptionViewer, known: readonly OwnOptionT[]): OwnOptionT[] {
    const fromRows = (input.option_rows ?? [])
        .filter(
            (row) =>
                row.is_private &&
                row.input_id === input.input_id &&
                ((viewer.userId && row.created_by === viewer.userId) ||
                    (viewer.entryId && row.entry_id === viewer.entryId))
        )
        .map((row) => ({ option_id: row.option_id, input_id: row.input_id as string, label: row.label }));
    const present = new Set(fromRows.map((option) => option.option_id));
    const fromKnown = known.filter((option) => option.input_id === input.input_id && !present.has(option.option_id));
    const deleted = new Set(known.filter((option) => option.deleted).map((option) => option.option_id));
    // A label known in this session (just renamed) is newer than the row loaded with the page.
    const labels = new Map(known.map((option) => [option.option_id, option.label]));
    return [...fromRows, ...fromKnown]
        .filter((option) => !deleted.has(option.option_id))
        .map(({ option_id, input_id, label }) => ({
            option_id,
            input_id,
            label: labels.get(option_id) ?? label,
        }));
}
